
import { useEffect, useState, useRef } from "react";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { useTranslation } from "react-i18next";
import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import CategoryService from "../../services/complaintCategory/category";
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";

const CategoryDatatable: React.FC<{ complaintCategories: any[] }> = ({ complaintCategories }) => {
  const { t } = useTranslation();
  const toast = useRef<Toast>(null);
  const [categories, setCategories] = useState<any[]>([]);
  const [selectedRow, setSelectedRow] = useState<any | null>(null);

  useEffect(() => {
    fetchCategory();
  }, []);


  useEffect(() => {
    setCategories(complaintCategories); // Sync with parent-provided filtered data
  }, [complaintCategories]);

  const fetchCategory = async () => {
    try {
      const response = await CategoryService.getComplaintCategory();
      setCategories(response);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const showMessage = (label: string, message: string, severity: any) => {
    toast.current?.show({
      severity,
      summary: label,
      detail: message,
      life: 3000,
    });
  };

  const nameEditor = (options: any) => (
    <InputText
      type="text"
      value={options.value}
      style={{ width: "95%" }}
      onChange={(e) => options.editorCallback!(e.target.value)}
    />
  );

  const subCategoryEditor = (options: any) => (
    <InputText
      type="text"
      value={options.value}
      style={{ width: "95%" }}
      onChange={(e) => options.editorCallback!(e.target.value)}
    />
  );

  const onRowEditComplete = async (event: DataTableRowEditCompleteEvent) => {
    const { newData, index } = event;
    const updatedCategories = [...categories];

  const newName = newData.complaintName?.trim().toLowerCase();
  if (!newName) {
    showMessage("Validation Error", "Category name cannot be empty!", "warn");
    return;
  }
  const isDuplicate = categories.some(
    (cat, i) =>
      i !== index &&
      cat.complaintName?.trim().toLowerCase() === newName
  );
  if (isDuplicate) {
    showMessage("Duplicate Entry", "Category name already exists!", "warn");
    return;
  }

    updatedCategories[index] = newData;
    setCategories(updatedCategories);

    try {
      const response = await CategoryService.updateComplaintCategory(newData);
      if (response) {
        showMessage("Update Category", "Category updated successfully!", "success");
        fetchCategory();
      } else {
        showMessage("Update Category", "Failed to update category.", "error");
      }
    } catch (error) {
      console.error("Error updating category:", error);
      showMessage("Update Category", "Error updating category.", "error");
    }
  };

  const confirmDelete = (rowData: any) => {
    setSelectedRow(rowData);
    confirmDialog({
      message: t("Are you sure you want to delete this category?"),
      header: t("Confirm Delete"),
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => handleDelete(rowData),
      reject: () => console.log("Delete cancelled"),
    });
  };

  const handleDelete = async (rowData: any) => {
    try {
      const response = await CategoryService.deleteComplaintCategory(rowData.complaintCategoryId);
      if (response) {
        setCategories(prev =>
          prev.filter((c) => c.complaintCategoryId !== rowData.complaintCategoryId)
        );
        showMessage("Delete Category", "Category deleted successfully!", "success");
      } else {
        showMessage("Delete Category", "Failed to delete category.", "error");
      }
    } catch (error) {
      console.error("Error deleting category:", error);
      showMessage("Delete Category", "Error deleting category.", "error");
    }
  };

  return (
    <div className="col-12">
      <Toast ref={toast} />
      <ConfirmDialog />
      <div className="card border-noround p-2">
        <DataTable
          value={categories}
          paginator
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          rows={10}
          stripedRows
          editMode="row"
          onRowEditComplete={onRowEditComplete}
            // paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                      paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"


          currentPageReportTemplate="{first} to {last} of {totalRecords}"
        >
          <Column field="complaintName" header={"Complaint Category"} editor={nameEditor} style={{ width: "25%" }} />
          <Column field="description" header={t("Description")} editor={subCategoryEditor} style={{ width: "25%" }} />
          <Column header={t("Edit")} rowEditor style={{ width: "8%" }} />
          <Column
            header={t("Delete")}
            body={(rowData) => (
              <Button
                icon="pi pi-trash"
                onClick={() => confirmDelete(rowData)}
                className="p-button-rounded p-button-text p-button-danger"
                tooltip="Delete"
              />
            )}
            style={{ width: "8%" }}
          />
        </DataTable>
      </div>
    </div>
  );
};

export default CategoryDatatable;

